import { action, observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { FormGroup, Input, Label } from 'reactstrap';

import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { PredefinedRoleModel } from '@app/Models/Models';

import { appStore } from '@app/Stores/AppStore';
import { permissionStore } from '@app/Stores/PermissionStore';

import { IModalDialogContent, ModalWindow, ModalDialogOptions, ModalButtonType } from '../Modal';
import { Loading } from '../Loading';
import PermissionsPicker from '../Pickers/PermissionsPicker';

type PredefinedRoleModelExt = PredefinedRoleModel & {
    showAll?: boolean;
};

const maxPermissionsNumPerRole = 4;

@observer
export class ImpersonationDialog extends React.Component<{}> implements IModalDialogContent<void> {
    private _store: ImpersonationStore = new ImpersonationStore();

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        const { onEnterImpersonation } = this._store;

        return {
            title: `Impersonation`,
            buttons: [
                {
                    title: 'Close',
                    type: ModalButtonType.Cancel,
                    onClick: () => {
                        window.close();
                    }
                },
                {
                    title: 'Impersonate',
                    type: ModalButtonType.Save,
                    isDisabled: !this._store.isFormValid,
                    onClick: onEnterImpersonation(() => {
                        window.close();
                    })
                }
            ],
            width: '560px',
            bodyClassName: 'impersonation-dialog'
        };
    }

    render() {
        const { roles, permissionIdsCsv, loader, onChangeRole, onSelectPermission } = this._store;

        return (
            <>
                <FormGroup>
                    {!!roles.length && (
                        <>
                            <div className="filters-list-item-title">Roles:</div>
                            {roles.map((r) => (
                                <Label key={r.codeName} className="checkbox impersonate-role">
                                    <Input
                                        type="checkbox"
                                        bsSize="sm"
                                        checked={this._store.isRoleSelected(r)}
                                        onChange={() => onChangeRole(r)}
                                    />
                                    {this._renderCustomLabel(r)}
                                </Label>
                            ))}
                        </>
                    )}
                </FormGroup>
                <FormGroup>
                    <div className="filters-list-item-title">
                        Permissions:
                    </div>
                    <div className="filters-list-item-field">
                        <PermissionsPicker
                            value={permissionIdsCsv}
                            loader={loader}
                            className="mw-100"
                            placeholder="Select or enter the encoded string..."
                            enableStrDecoding
                            onSelect={onSelectPermission}
                        />
                    </div>
                </FormGroup>
                <Loading loading={loader.isPending} />
            </>
        );
    }

    private _renderCustomLabel(role: PredefinedRoleModelExt) {
        return (
            <>
                <span className="d-inline">{role.codeName}{' '}</span>
                {!!role.permissionIds?.length && (
                    <>
                        <span className="custom-style">
                            ({this._getPermissionsFromRole(role)})
                        </span>
                        {role.permissionIds.length > maxPermissionsNumPerRole && !role.showAll && (
                            <span style={{ fontSize: 15 }} title="Show all" onClick={(e) => this._onClickShowAll(e, role)}>
                                ...
                            </span>
                        )}
                    </>
                )}
            </>
        );
    }

    private _getPermissionsFromRole({ permissionIds, showAll }: PredefinedRoleModelExt) {
        if (!permissionIds) return;

        const { permissions } = permissionStore;
        const permissionNames = permissionIds.map((id) => permissions.find(p => p.id === id)?.codeName);
        const splicedNames = permissionNames.length > maxPermissionsNumPerRole
            ? permissionNames.slice(0, maxPermissionsNumPerRole)
            : permissionNames;

        const result = showAll ? permissionNames : splicedNames;
        return result.join(', ');
    }

    @action
    private _onClickShowAll(e: React.MouseEvent, role: PredefinedRoleModelExt) {
        e.preventDefault();
        role.showAll = true;
    }
}

class ImpersonationStore {
    @observable roles: PredefinedRoleModelExt[] = permissionStore.roles;
    @observable selectedRoles: PredefinedRoleModelExt[] = [];
    @observable permissionIds: number[] = [];
    public loader: PromiseCompletion = new PromiseCompletion();

    constructor() {
        if (!permissionStore.permissions.length) {
            void permissionStore.loadPermissions();
        }
        if (!permissionStore.roles.length) {
            void permissionStore.loadRoles().then(() => (this.roles = permissionStore.roles));
        }
    }

    @computed
    get permissionIdsCsv(): string {
        return this.permissionIds.join(',');
    }

    @computed
    get isFormValid(): boolean {
        return this._encodedPermissions().length > 0;
    }

    isRoleSelected(role: PredefinedRoleModelExt) {
        return this.selectedRoles.includes(role);
    }

    @action.bound
    onChangeRole(role: PredefinedRoleModelExt) {
        const index = this.selectedRoles.indexOf(role);
        if (index > -1) {
            this.selectedRoles.splice(index, 1);
        } else {
            this.selectedRoles.push(role);
        }
    }

    @action.bound
    onSelectPermission(values: string | null) {
        this.permissionIds = values?.split(',').map(v => Number(v)).filter(v => !Number.isNaN(v)) || [];
    }

    onEnterImpersonation = (callback: () => void) => () => {
        const encodedPermissions = this._encodedPermissions();
        if (!encodedPermissions) return;

        const permissionIds = this._mergedPermissionIds();
        appStore.setPermissionImpersonation({ permissionIds, encoded: encodedPermissions });
        callback();
        location.reload();
    };

    private _mergedPermissionIds(): number[] {
        let permissionIds: number[] = [...this.permissionIds];
        this.selectedRoles.forEach(role => {
            if (role.permissionIds) {
                permissionIds = permissionIds.concat(role.permissionIds);
            }
        });
        return [...new Set(permissionIds)];
    }

    private _encodedPermissions(): string {
        const permissionIds = this._mergedPermissionIds();
        if (!permissionIds.length) return '';

        const bytes = permissionStore.convertPermissionIdsToBytes(permissionIds);
        return permissionStore.encodeBytesToBase64(bytes);
    }
}
